import { useEffect, useRef, useState, type CSSProperties, type TouchEvent as ReactTouchEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, MessageSquare, Rocket, Star } from 'lucide-react';
import { setCurrentSignupFlow, trackSignupEvent, trackSignupEventOnce } from '../../lib/signupAnalytics';
import {
  checkSignupEmailAvailable,
  signupEmailUnavailableMessage,
} from '../../lib/signupEmailVerification';

const SITE = 'https://www.nexusbookai.com';

/** Brand guidelines */
const NEXUS_TEAL = '#21C7D1';
const HORIZON_BLUE = '#3B82F6';
const BRAND_GRADIENT = `linear-gradient(135deg, ${NEXUS_TEAL} 0%, ${HORIZON_BLUE} 100%)`;
const BRAND_CTA_SHADOW = '0 8px 30px rgba(33,199,209,0.28), 0 8px 30px rgba(59,130,246,0.22)';
const HERO_GLOW =
  'radial-gradient(ellipse at center, rgba(33,199,209,0.28) 0%, rgba(59,130,246,0.12) 38%, transparent 70%)';

function isValidEmail(value: string): boolean {
  return /\S+@\S+\.\S+/.test(value);
}

const FOOTER_INDUSTRIES = [
  'Cleaning',
  'Construction & Contracting',
  'Electrical',
  'Handyman',
  'HVAC',
  'Landscaping',
  'Lawn Care',
  'Painting',
  'Pest Control',
  'Plumbing',
  'Pool Service',
  'Pressure Washing',
  'Roofing',
  'Tree Care',
  'Auto Detailing',
  'Glass & Glazing',
];

const FOOTER_FEATURES = [
  'Scheduling',
  'Invoicing & Payments',
  'CRM & Leads',
  'Online Booking',
  'Proposals & Quotes',
  'Field Management',
  'Team Tracking',
  'Marketing',
  'Automations',
  'AI Set Up Assistant',
  'Analytics',
];

const FOOTER_RESOURCES = [
  { label: 'Pricing', href: `${SITE}` },
  { label: 'Integrations', href: `${SITE}` },
  { label: 'Help Centre', href: `${SITE}` },
  { label: 'FAQ', href: `${SITE}` },
  { label: 'Support', href: 'mailto:hello@nexusbookai.com' },
  { label: 'Terms of Service', href: `${SITE}/terms-of-service` },
  { label: 'Privacy Policy', href: `${SITE}/privacy-policy` },
];

const FOOTER_COMPANY = [
  { label: 'About Nexus', href: `${SITE}` },
  { label: 'Become a Partner', href: `${SITE}` },
  { label: 'Contact', href: 'mailto:hello@nexusbookai.com' },
];

const STATS = [
  {
    title: '37% Growth',
    body: 'Grow your revenue an average of 37% in your first year.',
  },
  {
    title: '4x Faster',
    body: 'Get paid 4x faster when collecting payment online through Nexus.',
  },
  {
    title: '15% More Tips',
    body: 'Field teams using Nexus get tipped an average of 15% more.',
  },
  {
    title: '70% Faster',
    body: 'Create quotes 70% faster and offer online booking.',
  },
  {
    title: '32% More Referrals',
    body: 'Turn happy customers into a growth engine with a built-in referral program that tracks rewards and brings in warmer leads.',
  },
  {
    title: '43% More Returning Customers',
    body: 'Automated outreach brings past clients back — reminders, reactivation campaigns, and follow-ups that refill your calendar.',
  },
];

const RATING_BADGES: { store: string; icon: JSX.Element }[] = [
  {
    store: 'Apple App Store',
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current" aria-hidden>
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-1.98 1.08-3.13-1.05.05-2.31.7-3.06 1.58-.66.77-1.24 2-1.1 3.17 1.16.09 2.35-.6 3.08-1.62" />
      </svg>
    ),
  },
  {
    store: 'Google Play',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden>
        <path fill="#EA4335" d="M12 10.2V13.8h5.09c-.22 1.18-.9 2.18-1.92 2.85l3.1 2.4C20.1 17.25 21 14.85 21 12c0-.66-.06-1.3-.17-1.9z" />
        <path fill="#34A853" d="M12 21c2.43 0 4.47-.8 5.96-2.18l-3.1-2.4c-.86.58-1.96.92-2.86.92-2.2 0-4.07-1.49-4.74-3.49l-3.2 2.47C5.55 19.1 8.52 21 12 21z" />
        <path fill="#FBBC05" d="M7.26 13.85A5.4 5.4 0 016.98 12c0-.64.11-1.27.28-1.85l-3.2-2.47A9 9 0 003 12c0 1.45.35 2.82.96 4.03z" />
        <path fill="#4285F4" d="M12 6.66c1.32 0 2.5.45 3.44 1.35l2.58-2.58C16.46 3.9 14.43 3 12 3 8.52 3 5.55 4.9 4.06 7.97l3.2 2.47C7.93 8.44 9.8 6.66 12 6.66z" />
      </svg>
    ),
  },
];

const TESTIMONIALS = [
  {
    business: 'Vancity Glass',
    role: 'Owner',
    quote:
      'Ordering glass from our suppliers in the same app we run everything else from is a game changer. Our sales flow is sharper, we capture and book more leads, and dispatching the crew is almost too easy now.',
    image: '/signup-nexus/vancity-glass.jpg',
    imageAlt: 'Vancity Glass owner',
    // Portrait is sky-heavy; bias crop toward the subject.
    imagePosition: '55%',
  },
  {
    business: 'Luxe Bros Exterior Cleaning',
    role: 'Co-founder',
    quote:
      'Online booking and faster quoting changed our whole pipeline. We close more jobs and spend way less time chasing paperwork.',
    image: '/signup-nexus/luxe-bros.jpg',
    imageAlt: 'Luxe Bros Exterior Cleaning owners',
    imagePosition: 'top',
  },
  {
    business: 'Woodpro Tree Services Ltd.',
    role: 'Owner',
    quote:
      'Scheduling crews used to be chaos. With Nexus we run routes, invoices, and customer follow-ups without dropping the ball.',
    image: '/signup-nexus/woodpro.jpg',
    imageAlt: 'Arborist climbing a tree with safety gear',
    imagePosition: 'center',
  },
];

const HERO_BULLETS = [
  'Never lose another lead to a missed call.',
  'Book more jobs with online booking + quoting.',
  'Get paid faster with invoices & card payments.',
];

const FEATURES = [
  {
    lead: 'Attract new customers',
    rest: 'by allowing new leads to book through your website or Facebook page.',
  },
  {
    lead: 'Send professional looking invoices',
    rest: 'right from the mobile app. Customize your invoices by adding your branding and send with one click when the job is done.',
  },
  {
    lead: 'Manage cash flow and get paid faster',
    rest: 'with invoicing and payments — batch invoicing, automated follow-ups, and one-click card pay online. Nexus also syncs with QuickBooks Online.',
  },
  {
    lead: 'Deliver a great customer experience',
    rest: "with Nexus's client hub, branded communications, automated emails and texts, and custom checklists.",
  },
  {
    lead: 'Capture all client information in one place while on the go',
    rest: 'with full client history, job details, checklists, notes, time tracking, and GPS directions.',
  },
];

/**
 * Signup-nexus marketing landing.
 * CTA navigates to /signup-nexus/email (account steps live on that page).
 */
export default function SignupNexus() {
  const navigate = useNavigate();
  const exitTimerRef = useRef<number | null>(null);
  const [exiting, setExiting] = useState(false);
  const [heroEmail, setHeroEmail] = useState('');
  const [heroError, setHeroError] = useState('');
  const [heroChecking, setHeroChecking] = useState(false);
  const [footerEmail, setFooterEmail] = useState('');
  const [showStickyCta, setShowStickyCta] = useState(false);

  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [carouselDrag, setCarouselDrag] = useState(0); // px drag while swiping
  const [carouselAnimating, setCarouselAnimating] = useState(true);
  const testimonialStageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    active: boolean;
    startX: number;
    lastX: number;
    lastT: number;
    velocity: number;
  } | null>(null);
  const testimonialCount = TESTIMONIALS.length;

  useEffect(() => {
    setCurrentSignupFlow('nexus');
    trackSignupEventOnce('nexus:page_view:landing', {
      eventType: 'page_view',
      step: 'landing',
      flow: 'nexus',
    });
    trackSignupEventOnce('nexus:step_view:cta', {
      eventType: 'step_view',
      step: 'cta',
      flow: 'nexus',
    });
    return () => {
      if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
    };
  }, []);

  /** Shortest signed distance around the loop: … -1, 0, 1 … */
  const circularOffset = (index: number, active: number) => {
    let raw = index - active;
    if (raw > testimonialCount / 2) raw -= testimonialCount;
    if (raw < -testimonialCount / 2) raw += testimonialCount;
    return raw;
  };

  const goToTestimonial = (next: number) => {
    const wrapped = ((next % testimonialCount) + testimonialCount) % testimonialCount;
    setCarouselAnimating(true);
    setCarouselDrag(0);
    setTestimonialIndex(wrapped);
  };

  const getCoverflowStyle = (index: number): CSSProperties => {
    const stageW = testimonialStageRef.current?.clientWidth || 360;
    const dragNorm = carouselDrag / Math.max(stageW, 1); // +right swipe
    const offset = circularOffset(index, testimonialIndex) + dragNorm;
    const abs = Math.abs(offset);

    const translateX = offset * 40; // keep peeks on-screen; rotation sells depth
    const rotateY = offset * -48;
    const translateZ = -Math.min(abs, 1.5) * 190;
    const scale = Math.max(0.7, 1 - abs * 0.18);
    const opacity = abs > 1.4 ? 0 : Math.max(0.4, 1 - abs * 0.3);
    const zIndex = Math.round(20 - abs * 10);

    return {
      transform: `translateX(-50%) translateX(${translateX}%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
      opacity,
      zIndex,
      pointerEvents: abs < 0.55 ? 'auto' : 'none',
      transition: carouselAnimating && !dragRef.current?.active
        ? 'transform 480ms cubic-bezier(0.22, 1, 0.36, 1), opacity 380ms ease'
        : 'none',
      filter: abs > 0.2 ? `brightness(${Math.max(0.55, 1 - abs * 0.22)})` : undefined,
    };
  };

  const onCarouselPointerDown = (clientX: number) => {
    dragRef.current = {
      active: true,
      startX: clientX,
      lastX: clientX,
      lastT: performance.now(),
      velocity: 0,
    };
    setCarouselAnimating(false);
  };

  const onCarouselPointerMove = (clientX: number) => {
    const drag = dragRef.current;
    if (!drag?.active) return;
    const now = performance.now();
    const dx = clientX - drag.startX;
    const dt = Math.max(now - drag.lastT, 1);
    drag.velocity = (clientX - drag.lastX) / dt;
    drag.lastX = clientX;
    drag.lastT = now;
    setCarouselDrag(dx);
  };

  const onCarouselPointerUp = () => {
    const drag = dragRef.current;
    if (!drag?.active) return;
    const dx = drag.lastX - drag.startX;
    const velocity = drag.velocity;
    drag.active = false;
    dragRef.current = null;

    const stageW = testimonialStageRef.current?.clientWidth || 360;
    const threshold = stageW * 0.18;
    const flicked = Math.abs(velocity) > 0.45;
    let next = testimonialIndex;

    if (dx <= -threshold || (flicked && velocity < 0)) {
      next = testimonialIndex + 1;
    } else if (dx >= threshold || (flicked && velocity > 0)) {
      next = testimonialIndex - 1;
    }

    setCarouselAnimating(true);
    setCarouselDrag(0);
    goToTestimonial(next);
  };

  const scrollToTestimonial = (realIndex: number) => {
    goToTestimonial(realIndex);
  };

  // Non-passive touchmove so we can prevent vertical page scroll while swiping the carousel.
  useEffect(() => {
    const stage = testimonialStageRef.current;
    if (!stage) return;
    const onMove = (e: TouchEvent) => {
      const drag = dragRef.current;
      if (!drag?.active || !e.touches[0]) return;
      const x = e.touches[0].clientX;
      const dx = Math.abs(x - drag.startX);
      if (dx > 8) e.preventDefault();
      const now = performance.now();
      const totalDx = x - drag.startX;
      const dt = Math.max(now - drag.lastT, 1);
      drag.velocity = (x - drag.lastX) / dt;
      drag.lastX = x;
      drag.lastT = now;
      setCarouselDrag(totalDx);
    };
    stage.addEventListener('touchmove', onMove, { passive: false });
    return () => stage.removeEventListener('touchmove', onMove);
  }, []);

  // Sticky CTA once the hero form scrolls out of view (mobile ad traffic).
  useEffect(() => {
    const form = document.getElementById('nexus-signup-form');
    if (!form || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0.15 },
    );
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  const goToEmailPage = (opts?: { email?: string; startAt?: 'email' | 'password' }) => {
    if (exiting) return;
    setExiting(true);
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    exitTimerRef.current = window.setTimeout(
      () => {
        navigate('/signup-nexus/email', {
          state: {
            email: opts?.email,
            startAt: opts?.startAt ?? 'email',
          },
        });
      },
      reduceMotion ? 0 : 180,
    );
  };

  const submitEmailAndContinue = async (
    rawEmail: string,
    source: 'hero' | 'footer' | 'sticky',
  ): Promise<{ ok: true } | { ok: false; error: string }> => {
    const normalized = rawEmail.trim().toLowerCase();
    trackSignupEvent({
      eventType: 'click',
      step: 'cta',
      flow: 'nexus',
      data: { cta: `start_free_trial_${source}`, ...(normalized ? { email: normalized } : {}) },
    });

    if (!normalized || !isValidEmail(normalized)) {
      return { ok: false, error: 'Enter a valid email to start.' };
    }

    const availability = await checkSignupEmailAvailable(normalized);
    if (availability.available === false) {
      trackSignupEvent({
        eventType: 'error',
        step: 'email',
        flow: 'nexus',
        data: {
          reason: availability.reason === 'team_member' ? 'team_member_email' : 'email_in_use',
          email: normalized,
          source,
        },
      });
      return { ok: false, error: signupEmailUnavailableMessage(availability.reason) };
    }
    if (!availability.ok && availability.error && availability.error !== 'throttled') {
      return { ok: false, error: 'Could not verify that email. Please try again.' };
    }

    trackSignupEventOnce(`nexus:step_complete:email:${normalized}`, {
      eventType: 'step_complete',
      step: 'email',
      flow: 'nexus',
      data: { email: normalized, source },
    });
    goToEmailPage({ email: normalized, startAt: 'password' });
    return { ok: true };
  };

  const handleHeroCta = async (e: React.FormEvent) => {
    e.preventDefault();
    if (heroChecking || exiting) return;
    setHeroChecking(true);
    setHeroError('');
    try {
      const result = await submitEmailAndContinue(heroEmail, 'hero');
      if (!result.ok) setHeroError(result.error);
    } finally {
      setHeroChecking(false);
    }
  };

  const handleFooterCta = async (e: React.FormEvent) => {
    e.preventDefault();
    if (exiting) return;
    const normalized = footerEmail.trim().toLowerCase();
    // Empty footer field → open the email step (no availability check yet).
    if (!normalized) {
      trackSignupEvent({
        eventType: 'click',
        step: 'cta',
        flow: 'nexus',
        data: { cta: 'start_free_trial_footer' },
      });
      goToEmailPage();
      return;
    }

    const result = await submitEmailAndContinue(footerEmail, 'footer');
    if (!result.ok) {
      // Keep them on landing with the email prefilled on the email page if format-only miss;
      // for in-use emails, still route to email step so they can change it — but show via query?
      // Prefer staying put isn't possible for footer without footerError state — go to email page
      // with the address and let that step re-check / show the error.
      goToEmailPage({ email: normalized, startAt: 'email' });
    }
  };

  const handleStickyCta = () => {
    trackSignupEvent({
      eventType: 'click',
      step: 'cta',
      flow: 'nexus',
      data: { cta: 'sticky_start_free_trial' },
    });
    document.getElementById('nexus-signup-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => document.getElementById('nexus-hero-email')?.focus(), 400);
  };

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden nexus-landing-page"
      style={{
        background: '#050507',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateX(-56px)' : 'translateX(0)',
      }}
    >
      {/* Brand ambient glow — Nexus Teal → Horizon Blue */}
      <div
        className="pointer-events-none absolute left-1/2 top-[80px] -translate-x-1/2 h-[420px] w-[720px] rounded-full"
        style={{ background: HERO_GLOW }}
      />

      <div className="relative mx-auto w-full max-w-[560px] px-5 pt-10 pb-16 sm:pt-14">
        {/* Brand mark + wordmark */}
        <div className="flex justify-center mb-7">
          <div className="inline-flex items-center gap-2.5">
            <img
              src="/nexus-connect-icon.png"
              alt=""
              className="h-10 w-10 sm:h-12 sm:w-12 rounded-[10px] object-cover select-none"
              draggable={false}
              aria-hidden
            />
            <img
              src="/Nexus%20Full%20Logo%20(512%20x%20150%20px).png"
              alt="Nexus"
              className="h-11 sm:h-14 w-auto select-none"
              draggable={false}
            />
          </div>
        </div>

        <h1 className="text-center text-[30px] sm:text-[36px] font-extrabold leading-[1.15] tracking-tight text-white">
          Book more jobs.
          <br />
          Get paid faster.
        </h1>
        <p className="mt-3 text-center text-[15px] sm:text-base text-white/60">
          The all-in-one app for home service businesses. Free trial — no credit card.
        </p>

        <div className="mt-7">
          <ul className="mx-auto flex w-fit max-w-full flex-col items-start gap-2.5 px-2">
            {HERO_BULLETS.map((item) => (
              <li key={item} className="flex items-start gap-2.5">
                <span
                  className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border"
                  style={{
                    background: 'rgba(33,199,209,0.15)',
                    borderColor: 'rgba(33,199,209,0.35)',
                  }}
                >
                  <Check className="h-3 w-3" style={{ color: NEXUS_TEAL }} strokeWidth={3} />
                </span>
                <span className="text-[14px] sm:text-[15px] leading-snug text-white/80 text-left">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Capture email here — don't make people click through first */}
        <form
          id="nexus-signup-form"
          onSubmit={handleHeroCta}
          className="mt-8 sm:mt-9 w-full"
        >
          <label htmlFor="nexus-hero-email" className="sr-only">
            Work email
          </label>
          <input
            id="nexus-hero-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={heroEmail}
            onChange={(e) => {
              setHeroEmail(e.target.value);
              if (heroError) setHeroError('');
            }}
            placeholder="Enter your email"
            className="w-full h-12 px-4 rounded-xl border border-white/15 bg-white text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/45"
          />
          {heroError && (
            <p className="mt-2 text-center text-[13px] font-medium text-red-400" role="alert">
              {heroError}
              {heroError.toLowerCase().includes('already exists') && (
                <>
                  {' '}
                  <a href="/login" className="underline text-white font-semibold">
                    Log in
                  </a>
                </>
              )}
            </p>
          )}
          <button
            type="submit"
            disabled={exiting || heroChecking}
            className="mt-2.5 w-full h-12 rounded-xl text-white text-[16px] font-semibold transition-opacity hover:opacity-95 disabled:opacity-70"
            style={{ background: BRAND_GRADIENT, boxShadow: BRAND_CTA_SHADOW }}
          >
            {heroChecking ? 'Checking…' : 'Start free trial'}
          </button>
          <p className="mt-3 text-center text-[11px] leading-snug text-white/45">
            By clicking &quot;Start free trial&quot; you agree to our{' '}
            <a
              href={`${SITE}/terms-of-service`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white/70 hover:text-white/90"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href={`${SITE}/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white/70 hover:text-white/90"
            >
              Privacy Policy
            </a>
            .
          </p>
          <p className="mt-2 text-center text-[12px] text-white/45">
            No credit card required · Takes about 2 minutes
          </p>
        </form>

        {/* Social proof — app store ratings */}
        <div className="mt-12 grid grid-cols-2 gap-3">
          {RATING_BADGES.map((badge) => (
            <div
              key={badge.store}
              className="flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-4 text-center"
            >
              <span className="flex h-8 items-center justify-center text-white/90" aria-hidden>
                {badge.icon}
              </span>
              <div className="mt-2 flex gap-0.5" style={{ color: NEXUS_TEAL }} aria-hidden>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" strokeWidth={0} />
                ))}
              </div>
              <div className="mt-1.5 text-[13px] font-semibold text-white">5.0 rating</div>
            </div>
          ))}
        </div>

        {/* Trust + stats */}
        <section className="mt-14">
          <h2 className="text-center text-[22px] sm:text-[26px] font-bold leading-snug text-white">
            Trusted by home service professionals who use{' '}
            <span className="relative inline-block" style={{ color: NEXUS_TEAL }}>
              Nexus
              <span
                className="absolute left-0 right-0 -bottom-1 h-[6px] rounded-full"
                style={{ background: 'rgba(33, 199, 209, 0.35)' }}
                aria-hidden
              />
            </span>{' '}
            to run their business
          </h2>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STATS.map((stat) => (
              <div
                key={stat.title}
                className="rounded-2xl bg-white px-4 py-4 text-left shadow-[0_4px_24px_rgba(0,0,0,0.25)]"
              >
                <div className="text-[17px] font-bold text-gray-900">{stat.title}</div>
                <p className="mt-1.5 text-[13px] leading-snug text-gray-500">{stat.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mt-14">
          <h2 className="text-center text-[22px] sm:text-[26px] font-bold leading-snug text-white">
            Real businesses growing with{' '}
            <span style={{ color: NEXUS_TEAL }}>Nexus</span>
          </h2>
          <p className="mt-2 text-center text-[14px] text-white/50">
            Hear from service companies already running on the platform.
          </p>

          {/* Mobile: 3D coverflow carousel */}
          <div className="mt-8 lg:hidden -mx-3">
            <div
              ref={testimonialStageRef}
              className="nexus-coverflow relative mx-auto h-[430px] w-full select-none"
              style={{ perspective: '1200px', perspectiveOrigin: '50% 42%', touchAction: 'pan-y' }}
              onTouchStart={(e: ReactTouchEvent) => onCarouselPointerDown(e.touches[0].clientX)}
              onTouchEnd={onCarouselPointerUp}
              onTouchCancel={onCarouselPointerUp}
              onMouseDown={(e) => {
                e.preventDefault();
                onCarouselPointerDown(e.clientX);
              }}
              onMouseMove={(e) => {
                if (!dragRef.current?.active) return;
                onCarouselPointerMove(e.clientX);
              }}
              onMouseUp={onCarouselPointerUp}
              onMouseLeave={() => {
                if (dragRef.current?.active) onCarouselPointerUp();
              }}
              role="region"
              aria-roledescription="carousel"
              aria-label="Customer stories"
            >
              <div
                className="absolute inset-0"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {TESTIMONIALS.map((t, i) => (
                  <article
                    key={t.business}
                    className="nexus-coverflow-card absolute left-1/2 top-0 w-[72%] max-w-[300px] origin-center rounded-2xl bg-white overflow-hidden shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                    style={getCoverflowStyle(i)}
                    aria-hidden={i !== testimonialIndex}
                  >
                    <div className="aspect-[16/10] w-full overflow-hidden bg-gray-100">
                      <img
                        src={t.image}
                        alt={t.imageAlt}
                        className="h-full w-full object-cover pointer-events-none"
                        style={{ objectPosition: `center ${t.imagePosition}` }}
                        loading="lazy"
                        draggable={false}
                      />
                    </div>
                    <div className="px-4 py-4">
                      <p className="text-[14px] leading-relaxed text-gray-700 line-clamp-5">
                        &ldquo;{t.quote}&rdquo;
                      </p>
                      <div className="mt-3">
                        <div className="text-[14px] font-bold text-gray-900">{t.business}</div>
                        <div className="text-[12px] text-gray-500">{t.role}</div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2" aria-label="Testimonial slides">
              {TESTIMONIALS.map((t, i) => (
                <button
                  key={t.business}
                  type="button"
                  aria-label={`Show ${t.business}`}
                  aria-current={i === testimonialIndex}
                  onClick={() => scrollToTestimonial(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === testimonialIndex ? 'w-6' : 'w-2 bg-white/30'
                  }`}
                  style={i === testimonialIndex ? { background: NEXUS_TEAL } : undefined}
                />
              ))}
            </div>
          </div>

          {/* Desktop: all 3 on one row (break out of narrow column) */}
          <div className="mt-8 hidden lg:block">
            <div className="relative left-1/2 w-screen max-w-5xl -translate-x-1/2 px-6">
              <div className="grid grid-cols-3 gap-4">
                {TESTIMONIALS.map((t) => (
                  <article
                    key={t.business}
                    className="rounded-2xl bg-white overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.25)] flex flex-col"
                  >
                    <div className="aspect-[16/10] w-full overflow-hidden bg-gray-100">
                      <img
                        src={t.image}
                        alt={t.imageAlt}
                        className="h-full w-full object-cover"
                        style={{ objectPosition: `center ${t.imagePosition}` }}
                        loading="lazy"
                      />
                    </div>
                    <div className="px-4 py-4 flex flex-1 flex-col">
                      <p className="text-[13px] leading-relaxed text-gray-700 flex-1">
                        &ldquo;{t.quote}&rdquo;
                      </p>
                      <div className="mt-3">
                        <div className="text-[13px] font-bold text-gray-900">{t.business}</div>
                        <div className="text-[12px] text-gray-500">{t.role}</div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mid-page CTA — same email capture pattern */}
        <div className="mt-12 text-center">
          <button
            type="button"
            onClick={handleStickyCta}
            className="inline-flex h-12 items-center justify-center rounded-xl px-8 text-[15px] font-semibold text-white transition-opacity hover:opacity-95"
            style={{ background: BRAND_GRADIENT, boxShadow: BRAND_CTA_SHADOW }}
          >
            Start your free trial
          </button>
          <p className="mt-2 text-[12px] text-white/45">No credit card required</p>
        </div>

        {/* Features */}
        <section className="mt-14">
          <h2 className="text-[18px] sm:text-[20px] font-bold text-white leading-snug">
            Our features help you organize, manage, and grow your business:
          </h2>
          <ul className="mt-6 space-y-4">
            {FEATURES.map((feature) => (
              <li key={feature.lead} className="flex items-start gap-3">
                <span
                  className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border"
                  style={{
                    background: 'rgba(33,199,209,0.15)',
                    borderColor: 'rgba(33,199,209,0.35)',
                  }}
                >
                  <Check className="h-3 w-3" style={{ color: NEXUS_TEAL }} strokeWidth={3} />
                </span>
                <p className="text-[14px] sm:text-[15px] leading-relaxed text-white/80">
                  <span className="font-bold text-white">{feature.lead}</span> {feature.rest}
                </p>
              </li>
            ))}
          </ul>
        </section>

        {/* AI Setup */}
        <section className="mt-14">
          <div className="rounded-2xl bg-white px-5 py-6 sm:px-7 sm:py-8 shadow-[0_8px_40px_rgba(0,0,0,0.35)]">
            <div
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[12px] font-semibold"
              style={{
                background: 'rgba(33,199,209,0.12)',
                borderColor: 'rgba(33,199,209,0.35)',
                color: '#0e7490',
              }}
            >
              <Sparkles className="h-3.5 w-3.5" style={{ color: NEXUS_TEAL }} />
              Nexus AI Set Up
            </div>
            <h2 className="mt-4 text-[22px] sm:text-[26px] font-bold leading-snug text-gray-900">
              Skip the learning curve. Get set up fast.
            </h2>
            <p className="mt-3 text-[14px] sm:text-[15px] leading-relaxed text-gray-600">
              Most software leaves you staring at empty settings. Nexus AI Set Up walks new businesses
              through configuration step by step — so you&apos;re live, streamlined, and booking jobs
              without weeks of trial and error.
            </p>

            <div className="mt-6 space-y-3">
              {[
                {
                  icon: MessageSquare,
                  title: 'Guided, conversational setup',
                  body: 'Your AI assistant asks the right questions and configures Nexus around how you actually work.',
                },
                {
                  icon: Zap,
                  title: 'Skip the steep learning curve',
                  body: 'No manuals, no guessing. Get services, scheduling, and workflows in place in minutes — not months.',
                },
                {
                  icon: Rocket,
                  title: 'Go live faster, stay streamlined',
                  body: 'Launch with a system that already fits your business, so your team can focus on jobs — not software.',
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 rounded-xl bg-gray-50 border border-gray-100 px-3.5 py-3"
                  >
                    <span
                      className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{ background: 'rgba(59,130,246,0.12)', color: HORIZON_BLUE }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-[14px] font-bold text-gray-900">{item.title}</div>
                      <p className="mt-0.5 text-[13px] leading-snug text-gray-500">{item.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {/* Official footer — Jobber-style layout, Nexus branding */}
      <footer className="relative mt-8 border-t border-white/10 bg-[#0a0f1a]">
        {/* CTA band */}
        <div className="px-5 py-14 sm:py-16 text-center">
          <h2 className="text-[26px] sm:text-[34px] font-bold text-white tracking-tight">
            Try all Nexus features for free
          </h2>
          <form
            onSubmit={handleFooterCta}
            className="mt-7 mx-auto flex w-full max-w-xl flex-col sm:flex-row gap-2 sm:gap-0"
          >
            <input
              type="email"
              value={footerEmail}
              onChange={(e) => setFooterEmail(e.target.value)}
              placeholder="Work Email"
              className="flex-1 h-12 px-4 rounded-xl sm:rounded-r-none border border-white/15 bg-white text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
            />
            <button
              type="submit"
              className="h-12 px-6 rounded-xl sm:rounded-l-none text-white text-[15px] font-bold whitespace-nowrap transition-opacity hover:opacity-95"
              style={{ background: BRAND_GRADIENT }}
            >
              Start Free Trial
            </button>
          </form>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-[13px] text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" style={{ color: NEXUS_TEAL }} strokeWidth={3} />
              Put Nexus to work for you.
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" style={{ color: NEXUS_TEAL }} strokeWidth={3} />
              No credit card required.
            </span>
          </div>
        </div>

        {/* Link columns */}
        <div className="border-t border-white/10 px-5 py-12">
          <div className="mx-auto max-w-6xl grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6">
            <div className="col-span-2 md:col-span-3 lg:col-span-1">
              <div className="inline-flex items-center gap-2">
                <img
                  src="/nexus-connect-icon.png"
                  alt=""
                  className="h-7 w-7 rounded-md object-cover select-none"
                  draggable={false}
                  aria-hidden
                />
                <img
                  src="/Nexus%20Full%20Logo%20(512%20x%20150%20px).png"
                  alt="Nexus"
                  className="h-8 w-auto select-none"
                  draggable={false}
                />
              </div>
            </div>

            <div>
              <h3 className="text-[13px] font-bold text-white mb-3">Industries We Serve</h3>
              <ul className="space-y-1.5">
                {FOOTER_INDUSTRIES.map((item) => (
                  <li key={item}>
                    <a href="#nexus-signup-form" className="text-[12px] text-white/55 hover:text-white transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-[13px] font-bold text-white mb-3">Features</h3>
              <ul className="space-y-1.5">
                {FOOTER_FEATURES.map((item) => (
                  <li key={item}>
                    <a href="#nexus-signup-form" className="text-[12px] text-white/55 hover:text-white transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-[13px] font-bold text-white mb-3">Resources</h3>
              <ul className="space-y-1.5">
                {FOOTER_RESOURCES.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-[12px] text-white/55 hover:text-white transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-[13px] font-bold text-white mb-3">Company</h3>
              <ul className="space-y-1.5">
                {FOOTER_COMPANY.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-[12px] text-white/55 hover:text-white transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-[13px] font-bold text-white mb-3">Contact</h3>
              <a
                href="mailto:hello@nexusbookai.com"
                className="block text-[12px] text-white/55 hover:text-white transition-colors"
              >
                hello@nexusbookai.com
              </a>
              <div className="mt-5">
                <a
                  href="https://apps.apple.com/app/nexus-book-ai/id6758896840"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-black px-3 py-2 hover:border-white/40 transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white fill-current" aria-hidden>
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-1.98 1.08-3.13-1.05.05-2.31.7-3.06 1.58-.66.77-1.24 2-1.1 3.17 1.16.09 2.35-.6 3.08-1.62" />
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[9px] text-white/70">Download on the</div>
                    <div className="text-[13px] font-semibold text-white">App Store</div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-6xl mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/40">
            <span>© {new Date().getFullYear()} Nexus. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <a href={`${SITE}/terms-of-service`} target="_blank" rel="noopener noreferrer" className="hover:text-white/70">
                Terms
              </a>
              <a href={`${SITE}/privacy-policy`} target="_blank" rel="noopener noreferrer" className="hover:text-white/70">
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA once hero form is off-screen */}
      {showStickyCta && !exiting && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[#050507]/95 px-4 py-3 backdrop-blur-md sm:hidden"
          style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={handleStickyCta}
            className="w-full h-12 rounded-xl text-white text-[15px] font-semibold transition-opacity hover:opacity-95"
            style={{ background: BRAND_GRADIENT }}
          >
            Start free trial
          </button>
        </div>
      )}

      <style>{`
        .nexus-landing-page {
          transition: opacity 340ms cubic-bezier(0.22, 1, 0.36, 1),
            transform 340ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .nexus-coverflow {
          overflow: visible;
          cursor: grab;
        }
        .nexus-coverflow:active {
          cursor: grabbing;
        }
        .nexus-coverflow-card {
          will-change: transform, opacity;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        @media (prefers-reduced-motion: reduce) {
          .nexus-landing-page {
            transition: none !important;
          }
          .nexus-coverflow-card {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
